import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPrizeItemBody } from '../../../services/api/apiPayloads';
import {
    getMerchantInfo,
    getMerchants,
} from '../../../store/actions/merchantActions';
import {
    closePrizeEditor,
    createPrize,
    deletePrize,
    updateField,
    updatePrize,
} from '../../../store/actions/prizeActions';

const Prize = () => {
    const dispatch = useDispatch();
    const { prizeEditor, prizeItem } = useSelector((state) => state?.prize);
    const { merchantList, denominations } = useSelector(
        (state) => state?.merchant
    );
    const { hasReadOnly } = useSelector((state) => state?.auth?.user);
    const [isDisabled, setIsDisabled] = useState(false);

    const handleSave = (status) => {
        if (status === 'CANCEL') {
            dispatch(closePrizeEditor());
            return;
        }
        if (status === 'DELETE') {
            dispatch(deletePrize(prizeEditor.id));
            return;
        }
        const prizeBody = createPrizeItemBody(
            prizeItem.prizeType,
            prizeItem.merchantId,
            prizeItem.merchantName,
            prizeItem.denominationId,
            prizeItem.denominationValue,
            prizeItem.denominationQuantity,
            status
        );
        if (prizeEditor.id) {
            dispatch(updatePrize(prizeEditor.id, prizeBody));
        } else {
            dispatch(createPrize(prizeBody));
        }
    };
    const handleMerchantUpdate = (name, value) => {
        handleInputUpdate(name, value);
        dispatch(getMerchantInfo(value));
    };
    const handleInputUpdate = (name, value) => {
        dispatch(updateField(name, value));
    };
    useEffect(() => {
        setIsDisabled(
            hasReadOnly ||
                prizeItem.status === 'ACTIVE' ||
                prizeItem.status === 'EXPIRED'
        );
        dispatch(getMerchants());
        prizeEditor?.id && dispatch(getMerchantInfo(prizeItem?.merchantId));
    }, []);

    return (
        <Box
            sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                maxWidth: 800,
            }}
        >
            <FormControl fullWidth>
                <InputLabel
                    id="prize-type-label"
                    sx={(theme) => ({
                        backgroundColor: theme.palette.background.main,
                    })}
                >
                    Prize Type
                </InputLabel>
                <Select
                    value={prizeItem.prizeType}
                    onChange={(e) =>
                        handleInputUpdate('prizeType', e.target.value)
                    }
                    labelId="prize-type-label"
                    disabled={isDisabled}
                >
                    <MenuItem value="VOUCHER">Voucher</MenuItem>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="COIN">Coin</MenuItem>
                    <MenuItem value="RAFFLE">Raffle Entry</MenuItem>
                    <MenuItem value="PHYSICAL_PRIZE">Physical Prize</MenuItem>
                </Select>
            </FormControl>

            {prizeItem.prizeType === 'VOUCHER' && (
                <>
                    <FormControl fullWidth>
                        <InputLabel
                            sx={(theme) => ({
                                backgroundColor: theme.palette.background.main,
                            })}
                        >
                            Merchant
                        </InputLabel>
                        <Select
                            value={prizeItem.merchantId}
                            onChange={(e) =>
                                handleMerchantUpdate(
                                    'merchantId',
                                    e.target.value
                                )
                            }
                            disabled={isDisabled}
                            renderValue={() => prizeItem.merchantName}
                        >
                            {merchantList.map((m) => (
                                <MenuItem
                                    key={m.merchantId}
                                    value={m.merchantId}
                                >
                                    {m.merchantName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth disabled={!prizeItem.merchantId}>
                        <InputLabel
                            sx={(theme) => ({
                                backgroundColor: theme.palette.background.main,
                            })}
                        >
                            Denomination
                        </InputLabel>
                        <Select
                            value={prizeItem.denominationId}
                            onChange={(e) =>
                                handleInputUpdate(
                                    'denominationId',
                                    e.target.value
                                )
                            }
                            disabled={isDisabled}
                            renderValue={() =>
                                prizeItem.denominationValue
                                    ? `${prizeItem.merchantName} - ${prizeItem.denominationValue}`
                                    : ''
                            }
                        >
                            {(denominations || []).map((d) => (
                                <MenuItem
                                    key={d.denominationId}
                                    value={d.denominationId}
                                >
                                    {`${prizeItem.merchantName} - ${d.denominationName}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Denomination Quantity"
                        type="number"
                        value={prizeItem.denominationQuantity}
                        onChange={(e) =>
                            handleInputUpdate(
                                'denominationQuantity',
                                e.target.value
                            )
                        }
                        onWheel={(e) => e.target.blur()}
                        disabled={isDisabled}
                    />
                </>
            )}

            {prizeItem.prizeType === 'CASH' && (
                <TextField
                    fullWidth
                    label="Cash"
                    type="number"
                    value={prizeItem.denominationValue}
                    onChange={(e) =>
                        handleInputUpdate('denominationValue', e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                />
            )}
            {prizeItem.prizeType === 'COIN' && (
                <TextField
                    fullWidth
                    label="Coins"
                    type="number"
                    value={prizeItem.denominationValue}
                    onChange={(e) =>
                        handleInputUpdate('denominationValue', e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                />
            )}
            {prizeItem.prizeType === 'RAFFLE' && (
                <TextField
                    fullWidth
                    label="Prize Value"
                    type="number"
                    value={prizeItem.denominationValue}
                    onChange={(e) => {
                        if (e.target.value > 10) {
                            return;
                        }
                        handleInputUpdate('denominationValue', e.target.value);
                    }}
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                />
            )}
            {prizeItem.prizeType === 'PHYSICAL_PRIZE' && (
                <TextField
                    fullWidth
                    label="Prize Name"
                    type="text"
                    value={prizeItem.merchantName}
                    onChange={(e) =>
                        handleInputUpdate('merchantName', e.target.value)
                    }
                    disabled={isDisabled}
                />
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={() => handleSave('CANCEL')}>
                    Cancel
                </Button>
                {prizeItem.status === 'DRAFT' && !hasReadOnly && (
                    <Button
                        variant="outlined"
                        onClick={() => handleSave('DELETE')}
                    >
                        Delete
                    </Button>
                )}
                {(!prizeItem.status || prizeItem.status == 'DRAFT') &&
                    !hasReadOnly && (
                        <>
                            <Button
                                variant="outlined"
                                onClick={() => handleSave('DRAFT')}
                            >
                                Save as Draft
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleSave('ACTIVE')}
                            >
                                Publish
                            </Button>
                        </>
                    )}
            </Box>
        </Box>
    );
};

export default Prize;
